import {
  hasEntriesStillProcessing,
  listRecentMonitoringBatches,
  type MonitoringBatchSummary,
} from "./monitoring_service";

const POLL_INTERVAL_MS = 5_000;
// A batch still not pre_processed after this has stalled on the backend: stop waiting for it.
const BATCH_CREATION_LIMIT_MS = 2 * 60 * 60_000;
// Entering the Monitoring page re-checks at most this often (the count is the costly query).
const RECHECK_MIN_INTERVAL_MS = 60_000;

/**
 * unknown → nothing checked yet (no badge)
 * creating → polls the batch list until every batch is pre_processed
 * processing → polls the PROCESSING count until it reaches 0
 * idle → no polling
 */
export type MonitoringProcessingStatus = "unknown" | "creating" | "processing" | "idle";

type PollerState = {
  status: MonitoringProcessingStatus;
  pollingEnabled: boolean;
  pollIntervalMs: number;
  intervalId: ReturnType<typeof setInterval> | null;
  // Bumped on every phase change, so a check still in flight from the previous
  // phase is dropped.
  generation: number;
  checkInFlight: boolean;
  lastCheckAt: number | null;
};

function initialState(): PollerState {
  return {
    status: "unknown",
    pollingEnabled: false,
    pollIntervalMs: POLL_INTERVAL_MS,
    intervalId: null,
    generation: 0,
    checkInFlight: false,
    lastCheckAt: null,
  };
}

let state = initialState();
const listeners = new Set<() => void>();
const finishedListeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function emitFinished(): void {
  for (const listener of finishedListeners) listener();
}

export function isBusyStatus(status: MonitoringProcessingStatus): boolean {
  return status === "creating" || status === "processing";
}

function syncPolling(): void {
  const shouldPoll = state.pollingEnabled && state.status !== "idle";
  if (shouldPoll && state.intervalId == null) {
    state.intervalId = setInterval(() => void check(), state.pollIntervalMs);
  } else if (!shouldPoll && state.intervalId != null) {
    clearInterval(state.intervalId);
    state.intervalId = null;
  }
}

function setStatus(next: MonitoringProcessingStatus): void {
  if (next === state.status) return;
  state.status = next;
  syncPolling();
  emit();
}

function finish(): void {
  const wasBusy = isBusyStatus(state.status);
  setStatus("idle");
  if (wasBusy) emitFinished();
}

function isBatchCreating(batch: MonitoringBatchSummary, now: number): boolean {
  if (batch.preProcessed || !batch.uploadedAt) return false;
  const uploadedAt = Date.parse(batch.uploadedAt);
  return Number.isFinite(uploadedAt) && now - uploadedAt < BATCH_CREATION_LIMIT_MS;
}

/** One check for the current phase. A failed request just waits for the next one. */
async function check(): Promise<void> {
  if (state.checkInFlight) return;
  const generation = state.generation;
  state.checkInFlight = true;
  state.lastCheckAt = Date.now();
  try {
    // From unknown (app load) or idle (page entry) it resumes whatever is in flight.
    if (state.status !== "processing") {
      const batches = await listRecentMonitoringBatches();
      if (generation !== state.generation || !batches.ok) return;
      const now = Date.now();
      if (batches.value.some((batch) => isBatchCreating(batch, now))) {
        setStatus("creating");
        return;
      }
    }

    const processing = await hasEntriesStillProcessing();
    if (generation !== state.generation || !processing.ok) return;
    if (processing.value) setStatus("processing");
    else finish();
  } finally {
    if (generation === state.generation) state.checkInFlight = false;
  }
}

function startPhase(next: "creating" | "processing"): void {
  state.generation += 1;
  state.checkInFlight = false;
  setStatus(next);
  void check();
}

export function markMonitoringBatchUploaded(): void {
  startPhase("creating");
}

/** A single entry has no batch to wait for; a batch still being created keeps "creating". */
export function markMonitoringEntryAdded(): void {
  if (state.status === "creating") return;
  startPhase("processing");
}

/** Entering the Monitoring page: catch work started elsewhere (API, another tab, a colleague). */
export function recheckMonitoringProcessingIfIdle(): void {
  if (state.status !== "idle") return;
  if (state.lastCheckAt != null && Date.now() - state.lastCheckAt < RECHECK_MIN_INTERVAL_MS) return;
  void check();
}

export function startMonitoringProcessingPoll(intervalMs = POLL_INTERVAL_MS): void {
  if (state.pollingEnabled) return;
  state.pollingEnabled = true;
  state.pollIntervalMs = intervalMs;
  syncPolling();
  void check();
}

export function stopMonitoringProcessingPoll(): void {
  if (state.intervalId != null) clearInterval(state.intervalId);
  const wasUnknown = state.status === "unknown";
  state = { ...initialState(), generation: state.generation + 1 };
  if (!wasUnknown) emit();
}

export function subscribeMonitoringProcessing(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function subscribeMonitoringProcessingFinished(listener: () => void): () => void {
  finishedListeners.add(listener);
  return () => {
    finishedListeners.delete(listener);
  };
}

export function getMonitoringProcessingStatus(): MonitoringProcessingStatus {
  return state.status;
}
